import React, { useEffect, useState } from "react";
import { Users } from "lucide-react";
import axios from "axios";
import { Spinner } from "../components/ui/shadcn-io/spinner";
import { createEmployeeColumnsReadOnly } from "../lib/employeeTableColumnsReadOnly";
import { Employee } from "../lib/employeeTableColumns";
import { DataTable } from "../components/dataTable";

export default function EmployeeEmployees() {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      setIsLoading(true);
      setError("");

      const token = localStorage.getItem("authToken");
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}api/employees`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // Map backend data to frontend Employee type
      // Handle both response formats: { employees: [...] } or direct array
      const employeesData = response.data.employees || response.data || [];
      const mappedEmployees: Employee[] = employeesData.map((emp: any) => ({
        id: emp._id,
        name: emp.fullName,
        email: emp.email,
        phone: emp.phone,
        company: emp.company,
        employeeType: emp.employeeType || "Employee",
        isActive: emp.isActive !== undefined ? emp.isActive : true,
        resignationDate: emp.resignationDate,
        resignationReason: emp.resignationReason,
        lastWorkingDay: emp.lastWorkingDay,
      }));

      setEmployees(mappedEmployees);
    } catch (error: any) {
      console.error("Error fetching employees:", error);
      setError(error.response?.data?.message || "Failed to fetch employees");
    } finally {
      setIsLoading(false);
    }
  };

  const columns = createEmployeeColumnsReadOnly();

  if (isLoading) {
    return (
      <div className="flex flex-1 flex-col gap-4 p-4 pt-0">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Employees</h1>
            <p className="text-muted-foreground">
              View all team members and their information
            </p>
          </div>
        </div>
        <div className="flex items-center justify-center h-[400px]">
          <div className="text-center space-y-4">
            <Spinner variant="ellipsis" size={40} className="text-primary" />
            <div>
              <h3 className="text-lg font-medium">Loading employees...</h3>
              <p className="text-sm text-muted-foreground">
                Please wait while we fetch the data
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-4 p-4 pt-0">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h1 className="text-2xl font-bold tracking-tight">Employees</h1>
          <p className="text-muted-foreground">
            View all team members and their information
          </p>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <div className="flex items-center gap-2">
            <div className="h-4 w-4 rounded-full bg-red-500" />
            <p className="text-sm font-medium text-red-800">Error</p>
          </div>
          <p className="mt-1 text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Stats Cards */}
      {/* <div className="grid gap-4 md:grid-cols-3">
        <div className="rounded-lg border bg-card text-card-foreground shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                Total Employees
              </p>
              <p className="text-2xl font-bold">{employees.length}</p>
            </div>
            <Users className="h-8 w-8 text-blue-600" />
          </div>
        </div>

        <div className="rounded-lg border bg-card text-card-foreground shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                Active Employees
              </p>
              <p className="text-2xl font-bold">
                {employees.filter((emp) => emp.isActive).length}
              </p>
            </div>
            <Users className="h-8 w-8 text-green-600" />
          </div>
        </div>

        <div className="rounded-lg border bg-card text-card-foreground shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                Inactive Employees
              </p>
              <p className="text-2xl font-bold">
                {employees.filter((emp) => !emp.isActive).length}
              </p>
            </div>
            <Users className="h-8 w-8 text-red-600" />
          </div>
        </div>
      </div> */}

      {/* Data Table */}
      <DataTable columns={columns} data={employees} />
    </div>
  );
}
